import type { OpenWorldForkOrigin } from '@/lib/openWorlds/forks'
import { validateOpenWorldConfiguration } from '@/lib/openWorlds/configuration'
import type { OpenWorldConfiguration, OpenWorldRetrievalLease } from '@/lib/openWorlds/types'
import type { Persona } from '@/types/domain'
import type { WorldEntry } from '@/lib/worlds/types'
import type { OpenWorldConversationSettings } from '@/lib/openWorlds/conversation'
import type { AppImage } from '@/lib/media/imageAssetFields'
import { validOpenWorldAvatar } from '@/lib/openWorlds/avatar'

export const OPEN_WORLD_LIMITS = {
  titleBytes: 512,
  summaryBytes: 16 * 1024,
  contentBytes: 64 * 1024,
  entryIdBytes: 128,
  tagBytes: 256,
  botIdBytes: 128,
  tagsPerEntry: 32,
  seedEntries: 500,
  configurationJsonBytes: 256 * 1024,
  seedEntriesJsonBytes: 2 * 1024 * 1024,
} as const

export type OpenWorldPersistenceErrorCode =
  | 'unauthenticated'
  | 'not_found'
  | 'conflict'
  | 'invalid_input'
  | 'source_required'
  | 'source_unavailable'
  | 'source_unpublished'
  | 'source_invalid'
  | 'reload_required'
  | 'backend'

export interface OpenWorldValidationIssue {
  path: string
  message: string
}

export class OpenWorldPersistenceError extends Error {
  readonly code: OpenWorldPersistenceErrorCode
  readonly issues: OpenWorldValidationIssue[]
  override readonly cause?: unknown

  constructor(
    code: OpenWorldPersistenceErrorCode,
    message: string,
    options: { issues?: OpenWorldValidationIssue[]; cause?: unknown } = {},
  ) {
    super(message)
    this.name = 'OpenWorldPersistenceError'
    this.code = code
    this.issues = options.issues ? options.issues.map((item) => ({ ...item })) : []
    this.cause = options.cause
  }
}

export interface OpenWorldDefinitionContent {
  /** Absent or null inherits the original source World's current portrait. */
  avatar?: AppImage | null
  // Absent on legacy records; interpreted as an empty selection.
  initialActiveEntryIds?: string[]
  openingAlternatives?: string[]
  title: string
  summary: string
  sourceWorldId: number | null
  configuration: OpenWorldConfiguration
}

export interface OpenWorldDefinitionRecord extends OpenWorldDefinitionContent {
  id: string
  revision: number
  createdAt: string
  updatedAt: string
}

export interface OpenWorldDefinitionSummary {
  id: string
  title: string
  summary: string
  sourceWorldId: number | null
  revision: number
  createdAt: string
  updatedAt: string
}

export interface OpenWorldLegacyBaseline {
  initialRetrievalLeases?: OpenWorldRetrievalLease[]
  schemaVersion: 1
  title: string
  summary: string
  initialMessage: string
  seedEntries: WorldEntry[]
}

export interface OpenWorldSourceProvenance {
  worldId: number
  worldVersionId: number
  publishedAt: string
  draftRevision: number | null
  title: string
  summary: string
  contentFingerprint: string
}

export interface OpenWorldSourcedBaseline {
  initialRetrievalLeases?: OpenWorldRetrievalLease[]
  schemaVersion: 2
  title: string
  summary: string
  initialMessage: string
  source: OpenWorldSourceProvenance
  // Retained as a runtime name for the immutable entries seeded into a playthrough.
  // Setups no longer store or edit seed canon.
  seedEntries: WorldEntry[]
}

export type OpenWorldBaseline = OpenWorldLegacyBaseline | OpenWorldSourcedBaseline

export interface OpenWorldPersonaSnapshot extends Pick<Persona, 'id' | 'persona_name' | 'pronouns' | 'description' | 'imgpath' | 'img_focus'> {
  attachedAt: string
  attachedHeadId: string | null
}

export interface AttachOpenWorldPersonaRequest {
  playthroughId: string
  personaId: number
  expectedRevision: number
  expectedHeadId: string | null
}

export interface OpenWorldPlaythroughRecord {
  conversationSettings?: OpenWorldConversationSettings
  displayTitle?: string | null
  openingOverride?: string | null
  forkOrigin?: OpenWorldForkOrigin | null
  id: string
  definitionId: string
  definitionRevision: number
  baseline: OpenWorldBaseline
  persona: OpenWorldPersonaSnapshot | null
  revision: number
  activeHeadId: string | null
  pendingAttemptId: string | null
  createdAt: string
  updatedAt: string
}

export interface OpenWorldPlaythroughSummary {
  /** Setup title captured when this story began, independent of story renames. */
  setupTitle?: string
  preview?: string
  forkOrigin?: OpenWorldForkOrigin | null
  id: string
  definitionId: string
  definitionRevision: number
  title: string
  summary: string
  personaName: string | null
  revision: number
  activeHeadId: string | null
  pendingAttemptId: string | null
  createdAt: string
  updatedAt: string
}

export interface OpenWorldListCursor {
  sortValue?: string
  updatedAt: string
  id: string
}

export interface OpenWorldListRequest {
  limit?: number
  cursor?: OpenWorldListCursor | null
}

export interface OpenWorldPlaythroughListRequest extends OpenWorldListRequest {
  search?: string
  sort?: 'activity'|'created'|'title'
  definitionId?: string
}

export interface SaveOpenWorldDefinitionRequest extends OpenWorldDefinitionContent {
  id: string
  mutationId: string
  expectedRevision: number | null
}

export interface SaveOpenWorldDefinitionResponse {
  definition: OpenWorldDefinitionRecord
  replayed: boolean
}

export interface GetOpenWorldDefinitionResponse {
  definition: OpenWorldDefinitionRecord | null
}

export interface ListOpenWorldDefinitionsResponse {
  items: OpenWorldDefinitionSummary[]
  nextCursor: OpenWorldListCursor | null
}

export interface CreateOpenWorldPlaythroughRequest {
  openingIndex?: number
  personaId: number
  id: string
  definitionId: string
  expectedDefinitionRevision: number
}

export interface CreateOpenWorldPlaythroughResponse {
  playthrough: OpenWorldPlaythroughRecord
  replayed: boolean
}

export interface GetOpenWorldPlaythroughResponse {
  playthrough: OpenWorldPlaythroughRecord | null
}

export interface ListOpenWorldPlaythroughsResponse {
  items: OpenWorldPlaythroughSummary[]
  nextCursor: OpenWorldListCursor | null
}

export type OpenWorldValidationResult<T> =
  | { success: true; value: T }
  | { success: false; issues: OpenWorldValidationIssue[] }

type UnknownRecord = Record<string, unknown>

const encoder = new TextEncoder()
const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const byteLength = (value: string): number => encoder.encode(value).byteLength
const addIssue = (issues: OpenWorldValidationIssue[], path: string, message: string): void => {
  issues.push({ path, message })
}

function checkExactKeys(
  value: UnknownRecord,
  allowed: readonly string[],
  path: string,
  issues: OpenWorldValidationIssue[],
): void {
  const allowedSet = new Set(allowed)
  for (const key of Object.keys(value)) {
    if (!allowedSet.has(key)) addIssue(issues, path ? `${path}.${key}` : key, 'Unknown field.')
  }
  for (const key of allowed) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      addIssue(issues, path ? `${path}.${key}` : key, 'Required field is missing.')
    }
  }
}

function checkString(
  value: unknown,
  path: string,
  maximumBytes: number,
  issues: OpenWorldValidationIssue[],
): value is string {
  if (typeof value !== 'string') {
    addIssue(issues, path, 'Must be a string.')
    return false
  }
  if (byteLength(value) > maximumBytes) {
    addIssue(issues, path, `Must be at most ${maximumBytes} UTF-8 bytes.`)
    return false
  }
  return true
}

function validateSeedEntries(
  input: unknown,
  path: string,
  issues: OpenWorldValidationIssue[],
): WorldEntry[] | null {
  if (!Array.isArray(input)) {
    addIssue(issues, path, 'Must be an array.')
    return null
  }
  if (input.length > OPEN_WORLD_LIMITS.seedEntries) {
    addIssue(issues, path, `Must contain at most ${OPEN_WORLD_LIMITS.seedEntries} entries.`)
  }

  const ids = new Set<string>()
  const entries: WorldEntry[] = []
  Array.from(input).forEach((candidate, index) => {
    const entryPath = `${path}[${index}]`
    if (!isRecord(candidate)) {
      addIssue(issues, entryPath, 'Must be an object.')
      return
    }
    checkExactKeys(
      candidate,
      ['client_id', 'position', 'title', 'tags', 'content'],
      entryPath,
      issues,
    )
    const clientId = candidate.client_id
    const position = candidate.position
    const title = candidate.title
    const content = candidate.content
    const tags = candidate.tags
    const validId = checkString(
      clientId,
      `${entryPath}.client_id`,
      OPEN_WORLD_LIMITS.entryIdBytes,
      issues,
    )
    if (validId && !clientId.trim()) {
      addIssue(issues, `${entryPath}.client_id`, 'Must be nonblank.')
    } else if (validId && ids.has(clientId)) {
      addIssue(issues, `${entryPath}.client_id`, 'Must be unique within seedEntries.')
    } else if (validId) {
      ids.add(clientId)
    }
    const validPosition = Number.isSafeInteger(position) && (position as number) >= 0
    if (!validPosition) {
      addIssue(issues, `${entryPath}.position`, 'Must be a nonnegative safe integer.')
    }
    const validTitle = checkString(
      title,
      `${entryPath}.title`,
      OPEN_WORLD_LIMITS.titleBytes,
      issues,
    )
    const validContent = checkString(
      content,
      `${entryPath}.content`,
      OPEN_WORLD_LIMITS.contentBytes,
      issues,
    )
    let validTags = true
    if (!Array.isArray(tags)) {
      addIssue(issues, `${entryPath}.tags`, 'Must be an array of strings.')
      validTags = false
    } else {
      if (tags.length > OPEN_WORLD_LIMITS.tagsPerEntry) {
        addIssue(
          issues,
          `${entryPath}.tags`,
          `Must contain at most ${OPEN_WORLD_LIMITS.tagsPerEntry} tags.`,
        )
        validTags = false
      }
      Array.from(tags).forEach((tag, tagIndex) => {
        if (
          !checkString(tag, `${entryPath}.tags[${tagIndex}]`, OPEN_WORLD_LIMITS.tagBytes, issues)
        ) {
          validTags = false
        }
      })
    }
    if (
      validId &&
      clientId.trim() &&
      validPosition &&
      validTitle &&
      validContent &&
      validTags &&
      Array.isArray(tags)
    ) {
      entries.push({
        client_id: clientId,
        position: position as number,
        title,
        tags: [...tags] as string[],
        content,
      })
    }
  })

  try {
    if (byteLength(JSON.stringify(input)) > OPEN_WORLD_LIMITS.seedEntriesJsonBytes) {
      addIssue(
        issues,
        path,
        `Estimated serialized size must be at most ${OPEN_WORLD_LIMITS.seedEntriesJsonBytes} UTF-8 bytes; the server is authoritative.`,
      )
    }
  } catch {
    addIssue(issues, path, 'Must be JSON-serializable.')
  }
  return entries
}

function validateSourceWorldId(
  input: unknown,
  path: string,
  issues: OpenWorldValidationIssue[],
): number | null {
  if (input === null) return null
  if (!Number.isSafeInteger(input) || (input as number) < 1) {
    addIssue(issues, path, 'Must be null or a positive safe integer.')
    return null
  }
  return input as number
}

function validateConfigurationForStorage(
  input: unknown,
  path: string,
  issues: OpenWorldValidationIssue[],
): OpenWorldConfiguration | null {
  if (!isRecord(input)) {
    addIssue(issues, path, 'Must be an object.')
    return null
  }
  checkExactKeys(
    input,
    ['schemaVersion', 'storyteller', 'curator', 'synthesis', 'retrievalLifetimeTurns',
      ...(Object.hasOwn(input, 'useWorldSnapshot') ? ['useWorldSnapshot'] : [])],
    path,
    issues,
  )
  const nested: Array<[unknown, string, readonly string[]]> = [
    [
      input.storyteller,
      `${path}.storyteller`,
      ['botId', 'staticPrompt', 'startupPrompt', 'initialMessage',
        ...(isRecord(input.storyteller) && Object.hasOwn(input.storyteller, 'notesIntervalTurns') ? ['notesIntervalTurns'] : []),
        ...['notesPrompt', 'notesSystemPrompt', 'notesHistory', 'notesIncludeStorytellerPrompt', 'includeAllStoryNotes'].filter(key => isRecord(input.storyteller) && Object.hasOwn(input.storyteller, key))],
    ],
    [
      input.curator,
      `${path}.curator`,
      ['botId', 'instructions', 'waitTurns', 'minimumTurnInterval', 'historyMessageLimit',
        ...(isRecord(input.curator) && Object.hasOwn(input.curator, 'mode') ? ['mode'] : [])],
    ],
    [
      input.synthesis,
      `${path}.synthesis`,
      ['botId', 'instructions', 'waitTurns', 'minimumTurnInterval', 'historyMessageLimit'],
    ],
  ]
  for (const [value, nestedPath, keys] of nested) {
    if (isRecord(value)) checkExactKeys(value, keys, nestedPath, issues)
  }
  const storyteller = isRecord(input.storyteller) ? input.storyteller : null
  if (storyteller && storyteller.startupPrompt !== null && isRecord(storyteller.startupPrompt)) {
    checkExactKeys(
      storyteller.startupPrompt,
      ['text', 'expiresAfterTurns'],
      `${path}.storyteller.startupPrompt`,
      issues,
    )
  }

  if (storyteller) {
    for (const key of ['notesPrompt', 'notesSystemPrompt']) {
      if (Object.hasOwn(storyteller, key)) checkString(storyteller[key], `${path}.storyteller.${key}`, OPEN_WORLD_LIMITS.contentBytes, issues)
    }
    checkString(
      storyteller.staticPrompt,
      `${path}.storyteller.staticPrompt`,
      OPEN_WORLD_LIMITS.contentBytes,
      issues,
    )
    checkString(
      storyteller.initialMessage,
      `${path}.storyteller.initialMessage`,
      OPEN_WORLD_LIMITS.contentBytes,
      issues,
    )
    if (isRecord(storyteller.startupPrompt)) {
      checkString(
        storyteller.startupPrompt.text,
        `${path}.storyteller.startupPrompt.text`,
        OPEN_WORLD_LIMITS.contentBytes,
        issues,
      )
    }
  }
  for (const agentName of ['curator', 'synthesis'] as const) {
    const agent = isRecord(input[agentName]) ? input[agentName] : null
    if (agent)
      checkString(
        agent.instructions,
        `${path}.${agentName}.instructions`,
        OPEN_WORLD_LIMITS.contentBytes,
        issues,
      )
  }
  for (const [value, botPath] of [
    [storyteller?.botId, `${path}.storyteller.botId`],
    [isRecord(input.curator) ? input.curator.botId : undefined, `${path}.curator.botId`],
    [isRecord(input.synthesis) ? input.synthesis.botId : undefined, `${path}.synthesis.botId`],
  ] as const) {
    if (typeof value === 'string') checkString(value, botPath, OPEN_WORLD_LIMITS.botIdBytes, issues)
  }

  try {
    if (byteLength(JSON.stringify(input)) > OPEN_WORLD_LIMITS.configurationJsonBytes) {
      addIssue(
        issues,
        path,
        `Estimated serialized size must be at most ${OPEN_WORLD_LIMITS.configurationJsonBytes} UTF-8 bytes; the server is authoritative.`,
      )
    }
  } catch {
    addIssue(issues, path, 'Must be JSON-serializable.')
  }

  const normalized = validateOpenWorldConfiguration(input)
  if (!normalized.success) {
    for (const item of normalized.issues) {
      addIssue(issues, `${path}.${item.path}`, item.message)
    }
    return null
  }
  return structuredClone(normalized.value)
}

export function validateOpenWorldDefinitionContent(
  input: unknown,
): OpenWorldValidationResult<OpenWorldDefinitionContent> {
  const issues: OpenWorldValidationIssue[] = []
  if (!isRecord(input))
    return { success: false, issues: [{ path: 'definition', message: 'Must be an object.' }] }
  checkExactKeys(input, ['title', 'summary', 'sourceWorldId', 'configuration', ...(Object.hasOwn(input, 'avatar') ? ['avatar'] : []),...(Object.hasOwn(input,'openingAlternatives')?['openingAlternatives']:[]), ...(Object.hasOwn(input, 'initialActiveEntryIds') ? ['initialActiveEntryIds'] : [])], '', issues)
  if (Object.hasOwn(input, 'avatar') && !validOpenWorldAvatar(input.avatar)) {
    addIssue(issues, 'avatar', 'Choose an uploaded setup avatar with valid crop positioning.')
  }
  if (Object.hasOwn(input, 'initialActiveEntryIds')) {
    const ids = input.initialActiveEntryIds
    if (!Array.isArray(ids) || ids.length > 6 || new Set(ids).size !== ids.length
      || ids.some(id => typeof id !== 'string' || !id.trim() || byteLength(id) > OPEN_WORLD_LIMITS.entryIdBytes)) {
      addIssue(issues, 'initialActiveEntryIds', 'Choose up to six distinct World entry IDs within 128 UTF-8 bytes each.')
    }
  }
  if (input.openingAlternatives !== undefined) {
    if (!Array.isArray(input.openingAlternatives) || input.openingAlternatives.length>8 || byteLength(JSON.stringify(input.openingAlternatives))>262144) addIssue(issues,'openingAlternatives','Use up to eight opening alternatives within 262,144 UTF-8 bytes.')
    else input.openingAlternatives.forEach((opening,index)=>{
      if (!checkString(opening,`openingAlternatives.${index}`,65536,issues) || typeof opening!=='string' || !opening.trim()) addIssue(issues,`openingAlternatives.${index}`,'Write an opening within 65,536 UTF-8 bytes.')
    })
  }
  const titleOk = checkString(input.title, 'title', OPEN_WORLD_LIMITS.titleBytes, issues)
  const summaryOk = checkString(input.summary, 'summary', OPEN_WORLD_LIMITS.summaryBytes, issues)
  const sourceWorldId = validateSourceWorldId(input.sourceWorldId, 'sourceWorldId', issues)
  const configuration = validateConfigurationForStorage(
    input.configuration,
    'configuration',
    issues,
  )
  if (issues.length || !titleOk || !summaryOk || !configuration) {
    return { success: false, issues }
  }
  return {
    success: true,
    value: {
      title: input.title as string,
      summary: input.summary as string,
      sourceWorldId,
      configuration,
      ...(input.avatar === undefined ? {} : { avatar: JSON.parse(JSON.stringify(input.avatar)) as AppImage | null }),
      ...(input.initialActiveEntryIds === undefined ? {} : { initialActiveEntryIds: [...input.initialActiveEntryIds as string[]] }),
      ...(input.openingAlternatives===undefined?{}:{openingAlternatives:[...input.openingAlternatives as string[]]}),
    },
  }
}

export function validateOpenWorldBaseline(
  input: unknown,
): OpenWorldValidationResult<OpenWorldBaseline> {
  const issues: OpenWorldValidationIssue[] = []
  if (!isRecord(input))
    return { success: false, issues: [{ path: 'baseline', message: 'Must be an object.' }] }
  const schemaVersion = input.schemaVersion
  checkExactKeys(input, [...(schemaVersion === 2
    ? ['schemaVersion', 'title', 'summary', 'initialMessage', 'source', 'seedEntries']
    : ['schemaVersion', 'title', 'summary', 'initialMessage', 'seedEntries']),
  ...(Object.hasOwn(input, 'initialRetrievalLeases') ? ['initialRetrievalLeases'] : [])], 'baseline', issues)
  if (schemaVersion !== 1 && schemaVersion !== 2)
    addIssue(issues, 'baseline.schemaVersion', 'Must be 1 or 2.')
  const titleOk = checkString(input.title, 'baseline.title', OPEN_WORLD_LIMITS.titleBytes, issues)
  const summaryOk = checkString(
    input.summary,
    'baseline.summary',
    OPEN_WORLD_LIMITS.summaryBytes,
    issues,
  )
  const messageOk = checkString(
    input.initialMessage,
    'baseline.initialMessage',
    OPEN_WORLD_LIMITS.contentBytes,
    issues,
  )
  if (messageOk && !(input.initialMessage as string).trim()) {
    addIssue(issues, 'baseline.initialMessage', 'Must be nonblank.')
  }
  const seedEntries = validateSeedEntries(input.seedEntries, 'baseline.seedEntries', issues)
  if (Object.hasOwn(input, 'initialRetrievalLeases')) {
    const leases = input.initialRetrievalLeases
    const ids = new Set<string>()
    if (!Array.isArray(leases) || leases.length > 6) {
      addIssue(issues, 'baseline.initialRetrievalLeases', 'Must contain at most six initial canon leases.')
    } else for (const lease of leases) {
      if (!isRecord(lease)) {
        addIssue(issues, 'baseline.initialRetrievalLeases', 'Each initial lease must be an object.')
        continue
      }
      checkExactKeys(lease, ['ref', 'selectedAtTurn', 'lifetimeTurns'], 'baseline.initialRetrievalLeases', issues)
      if (isRecord(lease.ref)) checkExactKeys(lease.ref, ['source', 'entryId'], 'baseline.initialRetrievalLeases.ref', issues)
      const ref = isRecord(lease.ref) ? lease.ref : null
      if (!ref || ref.source !== 'seed' || typeof ref.entryId !== 'string'
        || !seedEntries?.some(entry => entry.client_id === ref.entryId)
        || ids.has(ref.entryId) || lease.selectedAtTurn !== 1
        || !Number.isSafeInteger(lease.lifetimeTurns) || (lease.lifetimeTurns as number) < 1) {
        addIssue(issues, 'baseline.initialRetrievalLeases', 'Use distinct captured canon entries with turn 1 and a positive safe-integer lifetime.')
      }
      if (ref && typeof ref.entryId === 'string') ids.add(ref.entryId)
    }
  }
  let source: OpenWorldSourceProvenance | null = null
  if (schemaVersion === 2) {
    if (!isRecord(input.source)) {
      addIssue(issues, 'baseline.source', 'Must be an object.')
    } else {
      checkExactKeys(input.source, [
        'worldId', 'worldVersionId', 'publishedAt', 'draftRevision', 'title', 'summary',
        'contentFingerprint',
      ], 'baseline.source', issues)
      const worldId = validateSourceWorldId(input.source.worldId, 'baseline.source.worldId', issues)
      const worldVersionId = validateSourceWorldId(
        input.source.worldVersionId,
        'baseline.source.worldVersionId',
        issues,
      )
      const draftRevision = input.source.draftRevision === null
        ? null
        : Number.isSafeInteger(input.source.draftRevision) && (input.source.draftRevision as number) >= 0
          ? input.source.draftRevision as number
          : (addIssue(
              issues,
              'baseline.source.draftRevision',
              'Must be null or a nonnegative safe integer.',
            ), null)
      const publishedAtOk = checkString(input.source.publishedAt, 'baseline.source.publishedAt', 128, issues)
      const sourceTitleOk = checkString(
        input.source.title,
        'baseline.source.title',
        OPEN_WORLD_LIMITS.titleBytes,
        issues,
      )
      const sourceSummaryOk = checkString(
        input.source.summary,
        'baseline.source.summary',
        OPEN_WORLD_LIMITS.summaryBytes,
        issues,
      )
      const fingerprintOk = checkString(
        input.source.contentFingerprint,
        'baseline.source.contentFingerprint',
        128,
        issues,
      )
      if (publishedAtOk && !Number.isFinite(Date.parse(input.source.publishedAt as string))) {
        addIssue(issues, 'baseline.source.publishedAt', 'Must be a valid timestamp.')
      }
      if (fingerprintOk && !(input.source.contentFingerprint as string).trim()) {
        addIssue(issues, 'baseline.source.contentFingerprint', 'Must be nonblank.')
      }
      if (worldId && worldVersionId && publishedAtOk && sourceTitleOk && sourceSummaryOk && fingerprintOk) {
        source = {
          worldId,
          worldVersionId,
          publishedAt: input.source.publishedAt as string,
          draftRevision,
          title: input.source.title as string,
          summary: input.source.summary as string,
          contentFingerprint: input.source.contentFingerprint as string,
        }
      }
    }
  }
  if (issues.length || !titleOk || !summaryOk || !messageOk || !seedEntries)
    return { success: false, issues }
  const common = {
    title: input.title as string,
    summary: input.summary as string,
    initialMessage: input.initialMessage as string,
    seedEntries,
    ...(input.initialRetrievalLeases === undefined ? {} : { initialRetrievalLeases: structuredClone(input.initialRetrievalLeases as OpenWorldRetrievalLease[]) }),
  }
  return schemaVersion === 2 && source
    ? { success: true, value: { schemaVersion: 2, ...common, source } }
    : { success: true, value: { schemaVersion: 1, ...common } }
}
